import { computed, onBeforeUnmount, ref, type Ref } from 'vue';

// The VP-style grid's frozen left block (ADR-0049): VP's column set and order,
// what the tenant shows, and what the user has hidden or resized. Hidden
// columns and widths are kept per browser, separately for narrow and wide
// screens; sort is not kept (useGridSort).

export type GridColumnKey = 'wbs1' | 'wbs1Name' | 'firm' | 'wbs2' | 'wbs2Name' | 'wbs3' | 'wbs3Name';

export interface GridColumn {
  key: GridColumnKey;
  label: string;
  width: number;
  minWidth: number;
  // Code columns read in the mono face.
  code: boolean;
  // Project is always shown: the row has no identity without it.
  locked: boolean;
}

type TenantFlag = 'wbs2' | 'wbs3' | 'firm';

interface ColumnDef {
  key: GridColumnKey;
  width: number;
  minWidth: number;
  code?: boolean;
  locked?: boolean;
  flag?: TenantFlag;
}

const COLUMNS: readonly ColumnDef[] = [
  { key: 'wbs1', width: 128, minWidth: 90, code: true, locked: true },
  { key: 'wbs1Name', width: 200, minWidth: 100 },
  { key: 'firm', width: 170, minWidth: 90, flag: 'firm' },
  { key: 'wbs2', width: 76, minWidth: 58, code: true, flag: 'wbs2' },
  { key: 'wbs2Name', width: 160, minWidth: 90, flag: 'wbs2' },
  { key: 'wbs3', width: 66, minWidth: 50, code: true, flag: 'wbs3' },
  { key: 'wbs3Name', width: 140, minWidth: 80, flag: 'wbs3' },
];

const KEYS = new Set<string>(COLUMNS.map(c => c.key));

// Below 768px only the codes a row needs to be told apart stay by default,
// narrowed to about 160px together so a phone still shows the days.
export const NARROW_HIDDEN: readonly GridColumnKey[] = ['wbs1Name', 'firm', 'wbs2Name', 'wbs3', 'wbs3Name'];
const NARROW_WIDTHS: Partial<Record<GridColumnKey, number>> = { wbs1: 96, wbs2: 64 };

export const COLUMNS_STORAGE_KEY = 'tc:grid-columns:v1';

type WidthClass = 'narrow' | 'wide';

interface ColumnPrefs {
  hidden: GridColumnKey[];
  widths: Partial<Record<GridColumnKey, number>>;
}

type StoredPrefs = Partial<Record<WidthClass, ColumnPrefs>>;

function defaults(cls: WidthClass): ColumnPrefs {
  return cls === 'narrow'
    ? { hidden: [...NARROW_HIDDEN], widths: { ...NARROW_WIDTHS } }
    : { hidden: [], widths: {} };
}

// Anything unreadable falls back to the defaults for that width, one width at
// a time, so a bad entry for one screen size does not wipe the other.
function parse(raw: string | null): StoredPrefs {
  if (!raw) return {};
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!data || typeof data !== 'object') return {};
  const out: StoredPrefs = {};
  for (const cls of ['narrow', 'wide'] as const) {
    const p = (data as Record<string, unknown>)[cls];
    if (!p || typeof p !== 'object') continue;
    const { hidden, widths } = p as { hidden?: unknown; widths?: unknown };
    if (!Array.isArray(hidden)) continue;
    const prefs: ColumnPrefs = {
      hidden: hidden.filter((k): k is GridColumnKey => typeof k === 'string' && KEYS.has(k)),
      widths: {},
    };
    if (widths && typeof widths === 'object') {
      for (const [k, v] of Object.entries(widths as Record<string, unknown>)) {
        if (KEYS.has(k) && typeof v === 'number' && Number.isFinite(v)) prefs.widths[k as GridColumnKey] = v;
      }
    }
    out[cls] = prefs;
  }
  return out;
}

function load(): StoredPrefs {
  try {
    return parse(localStorage.getItem(COLUMNS_STORAGE_KEY));
  } catch {
    return {};
  }
}

function save(prefs: StoredPrefs): void {
  try {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable: the change holds for this page only
  }
}

const NARROW_QUERY = '(max-width: 767.98px)';

function useNarrow(): Ref<boolean> {
  const narrow = ref(false);
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return narrow;
  const mq = window.matchMedia(NARROW_QUERY);
  narrow.value = mq.matches;
  const onChange = (e: MediaQueryListEvent) => { narrow.value = e.matches; };
  mq.addEventListener('change', onChange);
  onBeforeUnmount(() => mq.removeEventListener('change', onChange));
  return narrow;
}

export interface GridColumnsOptions {
  labels: () => { wbs1: string; wbs2: string; wbs3: string };
  tenant: () => { showWbs2: boolean; showWbs3: boolean; showFirmName: boolean };
}

export function useGridColumns(opts: GridColumnsOptions) {
  const narrow = useNarrow();
  const stored = ref<StoredPrefs>(load());
  const widthClass = computed<WidthClass>(() => (narrow.value ? 'narrow' : 'wide'));
  const prefs = computed<ColumnPrefs>(() => stored.value[widthClass.value] ?? defaults(widthClass.value));

  function label(key: GridColumnKey): string {
    const l = opts.labels();
    switch (key) {
      case 'wbs1': return l.wbs1;
      case 'wbs1Name': return `${l.wbs1} Name`;
      case 'firm': return 'Firm Name';
      case 'wbs2': return l.wbs2;
      case 'wbs2Name': return `${l.wbs2} Name`;
      case 'wbs3': return l.wbs3;
      case 'wbs3Name': return `${l.wbs3} Name`;
    }
  }

  function tenantShows(def: ColumnDef): boolean {
    if (!def.flag) return true;
    const t = opts.tenant();
    return def.flag === 'wbs2' ? t.showWbs2 : def.flag === 'wbs3' ? t.showWbs3 : t.showFirmName;
  }

  function toColumn(def: ColumnDef): GridColumn {
    const w = prefs.value.widths[def.key];
    return {
      key: def.key,
      label: label(def.key),
      width: w !== undefined && w >= def.minWidth ? Math.round(w) : def.width,
      minWidth: def.minWidth,
      code: !!def.code,
      locked: !!def.locked,
    };
  }

  // What the gear menu lists: every column the tenant shows. A column the
  // tenant turns off in VP is not the user's to bring back.
  const menuColumns = computed(() =>
    COLUMNS.filter(tenantShows).map(def => ({
      ...toColumn(def),
      hidden: !def.locked && prefs.value.hidden.includes(def.key),
    })),
  );

  const visibleColumns = computed<GridColumn[]>(() =>
    COLUMNS.filter(def => tenantShows(def) && (def.locked || !prefs.value.hidden.includes(def.key))).map(toColumn),
  );

  function update(next: ColumnPrefs): void {
    stored.value = { ...stored.value, [widthClass.value]: next };
    save(stored.value);
  }

  function setHidden(key: GridColumnKey, hidden: boolean): void {
    const def = COLUMNS.find(c => c.key === key);
    if (!def || def.locked) return;
    const rest = prefs.value.hidden.filter(k => k !== key);
    update({ ...prefs.value, hidden: hidden ? [...rest, key] : rest });
  }

  function setWidth(key: GridColumnKey, px: number): void {
    const def = COLUMNS.find(c => c.key === key);
    if (!def) return;
    update({ ...prefs.value, widths: { ...prefs.value.widths, [key]: Math.max(def.minWidth, Math.round(px)) } });
  }

  function resetWidth(key: GridColumnKey): void {
    const widths = { ...prefs.value.widths };
    delete widths[key];
    update({ ...prefs.value, widths });
  }

  function reset(): void {
    update(defaults(widthClass.value));
  }

  return { narrow, widthClass, menuColumns, visibleColumns, setHidden, setWidth, resetWidth, reset };
}
